import cx from "classnames";
import { Location } from "history";
import React from "react";

import BlogPost from "@/mk2/apps/blogs/components/BlogPost/BlogPost";
import { CommentForumPlaceholder } from "@/mk2/apps/blogs/components/Comment";
import { FeedItemLikedProfile } from "@/mk2/apps/blogs/components/FeedItemLikedProfile";
import { UserChip } from "@/mk2/apps/forum/components/UserChip";
import GroupGridItem from "@/mk2/apps/groups/components/GroupGridItem";
import { BtnType } from "@/mk2/components/Btn";
import ActionButton from "@/mk2/containers/ActionButton/ActionButton";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import Complaint from "@/mk2/containers/Complaint/Complaint";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Deletion from "@/mk2/containers/Deletion/Deletion";
import { DeletionTargetType } from "@/mk2/containers/Deletion/Deletion.schemas";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { UserAgentParsed } from "@/mk2/helpers/detects";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    BlogFeedItemEntity,
    BlogFeedItemType,
    BlogPostEntity,
    PostVisibility,
    UserEntity,
} from "@/mk2/schemas";
import {
    HistoryLocationWithState,
    RouteNavigateFunction,
    RoutePathPattern,
} from "@/mk2/services/browserHistory";
import {
    HOME_SHOW_MORE_POSTS,
    LIKES_MESSAGE,
    LIKES_PROFILE,
    USER_JOINS_THE_GROUP,
} from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import styles from "./BlogFeedPostsWithLoadMore.mscss";

type PageItemType = "adslot1" | "adslot2" | "adslot3" | "adslot4" | "post" | "feeditem";

export type BlogsFeedItemId = string | number;

interface PageItem {
    type: PageItemType;
    item?: any;
    commentsCount?: number;
    complaintsCount?: number;
    isComplaintOpened?: boolean;
}

interface OwnProps {
    commentsCounts: { [postId: number]: number };
    complaintsCounts: { [postId: number]: number };
    complaintsOpened: boolean[];
    errorMessage: string;
    imageComplaintsCounts?: { [imageId: number]: number };
    loadingState: LoadingState;
    location: Location;
    routeNavigate: RouteNavigateFunction;
    routePathPattern: RoutePathPattern;
    isMobile: boolean;
    posts?: BlogPostEntity[];
    feedItems?: BlogFeedItemEntity[];
    hasMore: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    showStrips: boolean;
    userAgent: UserAgentParsed;
    membershipsInGroups?: number[];
    triggerActionOfLoadMoreButton: string;

    onDelete?(postId: number);
    onIgnoreUser?(username: string);
    onLoad(after: BlogsFeedItemId);
    onPostToggleMenuMore(postId: number);
    onPostToggleVisibilityMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number, location: HistoryLocationWithState);
    onSetDisplayingAtFeed(postId: number, displaying: boolean);
    onSetVisibility?(postId: number, visibility: PostVisibility);
}

type Props = OwnProps;

class BlogFeedPostsWithLoadMore extends React.PureComponent<Props> {
    public render() {
        const { feedItems, loadingState, posts } = this.props;

        return (
            <LoadingSwitch
                loadingState={loadingState}
                onLoad={this.dummyLoad}
                onRenderInit={this.renderInit}
                onRenderSuccess={this.renderSuccess}
                hasData={(posts && posts.length > 0) || (feedItems && feedItems.length > 0)}
            />
        );
    }

    private renderInit = () => {
        const { isMobile } = this.props;
        return (
            <React.Fragment>
                {Array.from(Array(isMobile ? 6 : 30).keys()).map((i) => (
                    <CommentForumPlaceholder
                        key={`placeholder-${i}`}
                        className={cx(
                            styles.BlogFeedPostsWithLoadMore__commentPlaceholder,
                            i === 0 &&
                                styles[
                                    "BlogFeedPostsWithLoadMore__commentPlaceholder--hasAbove-Post"
                                ],
                        )}
                    />
                ))}
            </React.Fragment>
        );
    };

    private renderSuccess = () => {
        const {
            feedItems,
            hasMore,
            isMobile,
            location,
            posts,
            requestUser,
            requestUserPermissions,
            triggerActionOfLoadMoreButton,
        } = this.props;

        const pageItems = this.getPageItems();

        if ((!posts || posts.length === 0) && (!feedItems || feedItems.length === 0)) {
            return null;
        }

        const usedArray = [];
        for (let i = 0; i < pageItems.length; i++) {
            if (pageItems[i].type === "post") {
                usedArray.push(this.renderPostPageItem(pageItems[i], i, i < 2));
            } else if (pageItems[i].type === "feeditem") {
                usedArray.push(this.renderFeedItemPageItem(pageItems[i], i, i < 2));
            } else if (pageItems[i].type === "adslot1") {
                if (isMobile) {
                    usedArray.push(
                        <AdSlot
                            key={i}
                            zone="Blogs Feed - Mid 01 - Mobile"
                            location={location}
                            marginTopWithAd={10}
                        />,
                    );
                } else {
                    usedArray.push(
                        <AdSlot
                            key={i}
                            zone="Blogs Feed - Rectangle 1 (Desktop)"
                            location={location}
                            marginTopWithAd={10}
                        />,
                    );
                }
            } else if (pageItems[i].type === "adslot2") {
                if (isMobile) {
                    usedArray.push(
                        <AdSlot
                            key={i}
                            zone="Blogs Feed - Mid 02 - Mobile"
                            location={location}
                            marginTopWithAd={10}
                        />,
                    );
                } else {
                    usedArray.push(
                        <AdSlot
                            key={i}
                            zone="Blogs Feed - Rectangle 3 (Desktop)"
                            location={location}
                            marginTopWithAd={10}
                        />,
                    );
                }
            }
        }

        return (
            <React.Fragment>
                <span id="topPager" />
                {usedArray}
                {hasMore && (
                    <div className={styles.BlogFeedPostsWithLoadMore__loadMore}>
                        <ActionButton
                            type={BtnType.Blue}
                            label={HOME_SHOW_MORE_POSTS}
                            triggerAction={triggerActionOfLoadMoreButton}
                            onClick={this.handleLoadMore}
                            showSpinnerWhenBusy
                        />
                    </div>
                )}
            </React.Fragment>
        );
    };

    private renderPostPageItem = (pageItem: PageItem, idx: number, forceEagerImages: boolean) => {
        const {
            imageComplaintsCounts,
            isMobile,
            location,
            routeNavigate,
            routePathPattern,
            onDelete,
            onIgnoreUser,
            onPostToggleMenuMore,
            onPostToggleVisibilityMenuMore,
            onReport,
            onSetDisplayingAtFeed,
            onSetVisibility,
            requestUser,
            requestUserPermissions,
            showStrips,
            userAgent,
        } = this.props;

        const post = pageItem.item as BlogPostEntity;

        return (
            <div
                key={idx}
                className={cx(
                    styles.BlogFeedPostsWithLoadMore__post,
                    pageItem.isComplaintOpened &&
                        styles["BlogFeedPostsWithLoadMore__post--complaintOpened"],
                )}
            >
                <Complaint
                    id={post.id}
                    entity={ComplaintTargetType.PhotoblogPost}
                    className={styles.BlogFeedPostsWithLoadMore__post__complaint}
                    scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                    inForum={false}
                    inGroups={false}
                />

                <Deletion
                    id={post.id}
                    entity={DeletionTargetType.PhotoblogPost}
                    className={styles.BlogFeedPostsWithLoadMore__post__complaint}
                    scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                />

                <BlogPost
                    className={cx(
                        pageItem.isComplaintOpened &&
                            styles["BlogFeedPostsWithLoadMore__post__blogPost--complaintOpened"],
                    )}
                    commentsCount={pageItem.commentsCount}
                    complaintsCount={pageItem.complaintsCount}
                    forceEagerImages={forceEagerImages}
                    imageComplaintsCounts={imageComplaintsCounts}
                    isMobile={isMobile}
                    location={location}
                    routeNavigate={routeNavigate}
                    routePathPattern={routePathPattern}
                    onDelete={onDelete}
                    onIgnoreUser={onIgnoreUser}
                    onReport={onReport}
                    onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                    onSetVisibility={onSetVisibility}
                    onToggleMenuMore={onPostToggleMenuMore}
                    onToggleVisibilityMenuMore={onPostToggleVisibilityMenuMore}
                    post={post}
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    showStrip={showStrips}
                    userAgent={userAgent}
                />
            </div>
        );
    };

    private renderFeedItemPageItem = (
        pageItem: PageItem,
        idx: number,
        forceEagerImages: boolean,
    ) => {
        const {
            imageComplaintsCounts,
            isMobile,
            location,
            routeNavigate,
            routePathPattern,
            membershipsInGroups,
            onDelete,
            onIgnoreUser,
            onPostToggleMenuMore,
            onPostToggleVisibilityMenuMore,
            onReport,
            onSetDisplayingAtFeed,
            onSetVisibility,
            requestUser,
            requestUserPermissions,
            showStrips,
            userAgent,
        } = this.props;

        const feedItem = pageItem.item as BlogFeedItemEntity;
        const hasFeedItemHeadline = feedItem.itemType !== BlogFeedItemType.POST;
        return (
            <div
                key={idx}
                className={cx(
                    styles.BlogFeedPostsWithLoadMore__post,
                    pageItem.isComplaintOpened &&
                        styles["BlogFeedPostsWithLoadMore__post--complaintOpened"],
                )}
            >
                {feedItem.post && (
                    <Complaint
                        id={feedItem.post.id}
                        entity={ComplaintTargetType.PhotoblogPost}
                        className={styles.BlogFeedPostsWithLoadMore__post__complaint}
                        scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                        inForum={false}
                        inGroups={false}
                    />
                )}

                {feedItem.post && (
                    <Deletion
                        id={feedItem.id}
                        entity={DeletionTargetType.PhotoblogPost}
                        className={styles.BlogFeedPostsWithLoadMore__post__complaint}
                        scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                    />
                )}

                {feedItem.itemType === BlogFeedItemType.POST_LIKE && (
                    <div
                        className={cx(
                            styles.BlogFeedPostsWithLoadMore__post__feedItemHeadline,
                            pageItem.isComplaintOpened &&
                                styles["BlogFeedPostsWithLoadMore__post--squareTopCorners"],
                        )}
                    >
                        <UserChip user={feedItem.user} size={24} spaceBetween={4} boldUsername />
                        &nbsp;
                        <span className="actor-action">{LIKES_MESSAGE}</span>
                    </div>
                )}

                {feedItem.itemType === BlogFeedItemType.PROFILE_LIKE && (
                    <div
                        className={cx(
                            styles.BlogFeedPostsWithLoadMore__post__feedItemHeadline,
                            pageItem.isComplaintOpened &&
                                styles["BlogFeedPostsWithLoadMore__post--squareTopCorners"],
                        )}
                    >
                        <UserChip user={feedItem.user} size={24} spaceBetween={4} boldUsername />
                        &nbsp;
                        <span className="actor-action">{LIKES_PROFILE}</span>
                    </div>
                )}

                {feedItem.itemType === BlogFeedItemType.GROUP_MEMBER && (
                    <div
                        className={cx(
                            styles.BlogFeedPostsWithLoadMore__post__feedItemHeadline,
                            pageItem.isComplaintOpened &&
                                styles["BlogFeedPostsWithLoadMore__post--squareTopCorners"],
                        )}
                    >
                        <UserChip user={feedItem.user} size={24} spaceBetween={4} boldUsername />
                        &nbsp;
                        <span className="actor-action">{USER_JOINS_THE_GROUP}</span>
                    </div>
                )}

                {feedItem.groupMember && feedItem.group && (
                    <div>
                        <GroupGridItem
                            className={
                                styles.BlogFeedPostsWithLoadMore__post__feedItemTargetWrapper
                            }
                            activeMembersCount={feedItem.group.activeMembersCount}
                            coverPhoto={feedItem.group.coverPhoto}
                            description={feedItem.group.description}
                            id={feedItem.group.id}
                            isMember={membershipsInGroups.includes(feedItem.group.id)}
                            location={location}
                            locked={feedItem.group.locked}
                            title={feedItem.group.name}
                            onJoinGroup={null}
                            requestUser={requestUser}
                            horizontal
                        />
                    </div>
                )}

                {feedItem.profile && (
                    <div>
                        <FeedItemLikedProfile
                            className={
                                styles.BlogFeedPostsWithLoadMore__post__feedItemTargetWrapper
                            }
                            user={feedItem.profile}
                            userDomainData={feedItem.profileUserDomainData}
                        />
                    </div>
                )}

                {feedItem.post && (
                    <BlogPost
                        className={cx(
                            (pageItem.isComplaintOpened || hasFeedItemHeadline) &&
                                styles["BlogFeedPostsWithLoadMore__post--squareTopCorners"],
                            pageItem.isComplaintOpened &&
                                styles[
                                    "BlogFeedPostsWithLoadMore__post__blogPost--complaintOpened"
                                ],
                        )}
                        commentsCount={pageItem.commentsCount}
                        complaintsCount={pageItem.complaintsCount}
                        forceEagerImages={forceEagerImages}
                        imageComplaintsCounts={imageComplaintsCounts}
                        isMobile={isMobile}
                        location={location}
                        routeNavigate={routeNavigate}
                        routePathPattern={routePathPattern}
                        onDelete={onDelete}
                        onIgnoreUser={onIgnoreUser}
                        onReport={onReport}
                        onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                        onSetVisibility={onSetVisibility}
                        onToggleMenuMore={onPostToggleMenuMore}
                        onToggleVisibilityMenuMore={onPostToggleVisibilityMenuMore}
                        parentFeedItem={feedItem}
                        post={feedItem.post}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        showStrip={showStrips}
                        userAgent={userAgent}
                    />
                )}
            </div>
        );
    };

    private dummyLoad = () => {
        return null;
    };

    private getPageItems = (): PageItem[] => {
        const ADSLOT_INDEXES = [2, 14];

        const {
            commentsCounts,
            complaintsCounts,
            complaintsOpened,
            feedItems,
            posts,
            requestUser,
        } = this.props;

        if (posts && feedItems) {
            throw new Error("define posts or feedItems, but not both");
        }

        const items: PageItem[] = posts
            ? posts.map(
                  (post, idx): PageItem => ({
                      type: "post",
                      item: post,
                      commentsCount: commentsCounts[post.id],
                      complaintsCount: complaintsCounts[post.id],
                      isComplaintOpened: complaintsOpened[idx],
                  }),
              )
            : feedItems.map(
                  (feedItem, idx): PageItem => ({
                      type: "feeditem",
                      item: feedItem,
                      commentsCount: feedItem.post ? commentsCounts[feedItem.post.id] : 0,
                      complaintsCount: feedItem.post ? complaintsCounts[feedItem.post.id] : 0,
                      isComplaintOpened: complaintsOpened[idx],
                  }),
              );

        if (hasAds(requestUser)) {
            ADSLOT_INDEXES.forEach((idx, i) => {
                if (items.length >= idx + 2) {
                    items.splice(idx + i /* +i for previous inserts */, 0, {
                        type: `adslot${i + 1}` as PageItemType,
                    });
                }
            });
        }

        return items;
    };

    private handleLoadMore = () => {
        const { feedItems, hasMore, onLoad } = this.props;
        const after = feedItems[feedItems.length - 1].id;
        if (hasMore) {
            onLoad(after);
        }
    };
}

export default BlogFeedPostsWithLoadMore;
